/* eslint-disable */
/* ==========================================================================
   Aero glass engine (V3), ported from the website's shared/glass-v3.js (25 Oct 2026, the go-live glass).
   Kept as close to the website copy as possible so the two never drift: the ONLY differences are the
   two blocks marked "APP ADDITION" (the size-class prefix and release()), and this wrapper, which turns
   the self-running script into installGlassV3(window, document), called once by ./index.js.
   Do not tune numbers here. The look is set in ./glassSettings.js (the app's glass-settings.js).
   eslint is disabled for this file only because it is a verbatim port of a browser script.
   ========================================================================== */
/* Aero glass button lab, V3 From research: Snell-law squircle refraction, Fresnel-weighted reflection and a key-light specular lip.
   Standalone, no dependencies. Auto-initialises on [data-glass-v3="primary|secondary|dark"] (data-fill="#hex", data-strength="1").
   window.GlassV3(el, opts), GlassV3.refresh(). Global intensity: window.GLASS_INTENSITY (0..3, default 1). Strength: window.GLASS_MODE = 'subtle' | 'liquid'.
   Generated by build.mjs from src/core.js + src/v3.js.
   HOVER LAB COPY (research/web3/glass-lab, 24 Oct 2026). Changes from the source file:
   - no scale on hover or press: the button never moves or changes size, only its light does
   - data-hover="key|lift|shimmer|press|bright|lens" picks a hover; data-press="soft|flat|none" the press
   - a caustic: light through the key-side lip focuses into a soft band inside the opposite lip
   - :focus-visible gets the hover; Enter/Space gets the press; data-force="hover|active" holds a state
   - reduced motion (or window.GLASS_REDUCED): states snap, the light stays at rest, the shimmer stops
   - WEB3 SHARED COPY: adds data-hover="keylift" (key light follows + Lift's deeper bend) and makes it the default
     when data-hover is absent; data-hover="base" turns hover off
   - data-px / data-py (0..1): where the light rests for keyboard focus and held states
   ROUND 5 (web3/glass-r5, 24 Oct 2026 evening):
   - the caustic is back. Round 4 lost it in the sampling: a wash is a stack of radial gradients, and every stop
     of the whole background was averaged into ONE colour (blue + peach + green + white = a near-grey), which
     scored zero as "not colourful" and was then removed again as ambient. Gradients are now read at the point
     each sample lands, pale hues count (in proportion), the hue is lifted to a visible chroma at the glass, and
     the caustic band is tinted by it (it was white, so on white glass it was white on white)
   - slab: a flat face, a short steep bevel and a visible wall of thickness along the bottom, lit by what is near
   - hover "keylift" (the default): key light follows, toned down, plus Lift. Ink glass gets Lift only.
   - .site-nav is frosted glass with a refracting lower edge (see aero.css); buttons in it read the page under it */
/* SHARED COMPONENT (web3/shared, 25 Oct 2026): one glass engine for every page. Its numbers come from ONE file,
   shared/glass-settings.js (window.GLASS_P, the glass panel's JSON). Load that file first. See shared/GLASS.md. */
export default function installGlassV3(W, D) {
  'use strict';
  var PFX = 'g3', ATTR = 'data-glass-v3';
  // APP ADDITION (figmaApp): the class that carries the size (.sm/.md/.lg). '.btn' on the website; the app sets
  // window.GLASS_BTN_CLASS = '.aero-g3' because '.btn' is Bootstrap's class in this codebase.
  var BTN = W.GLASS_BTN_CLASS || '.btn';
  /* ===== shared core (copied into every variant by build.mjs) ===== */
  var RM = W.matchMedia ? W.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
  var UA = navigator.userAgent;
  // SVG filters inside backdrop-filter only render in Chromium. Safari reports support and draws nothing,
  // Firefox accepts the syntax and draws nothing, so a UA check is needed on top of CSS.supports.
  var REFRACT = !!(W.CSS && CSS.supports && CSS.supports('backdrop-filter', 'url(#a)')) && /Chrome\/\d/.test(UA) && !/Firefox\//.test(UA);
  var WHITE = [255, 255, 255], INK = [29, 29, 31];
  var probe = D.createElement('canvas').getContext('2d', { willReadFrequently: true });
  var colCache = {}, pxCache = new WeakMap();

  /* Every tuning number reads P(name) from window.GLASS_P (shared/glass-settings.js). The defaults below are the
     panel's fallbacks (round 5) for any key the settings leave out. After changing GLASS_P at runtime, bump
     window.GLASS_P_V and call GlassV3.refresh(). */
  var PD = {
    reach: 340, paleLo: .012, paleHi: .1, boost: 2.2, ambOut: .55, refl: 1, pick: 1,
    glowW: .22, glowI: .27, glowWidth: 3.6,
    causW: .62, causI: .34, causWidth: 2.6, causPos: 2.4, causTint: 1, causEnv: .7, causDown: .75, causPow: 1.1,
    bevel: 7, wall: 3.3, wallW: .55, wallI: .5, wallTint: 1, crestW: .75, crestI: .2,
    refrW: 18, refrI: 14, refrBlur: .3, refrSat: 1.5,
    keyW: .7, keyI: .5, keyTight: 3,
    followW: 1, followI: 0, keyHW: .3, keyHI: .12, glowHW: .35, glowHI: 0, glowSize: .7, spreadW: .25, spreadI: .4, followSpeed: 90,
    liftBend: .3, liftRim: .4, liftWall: .35, liftShadow: 1, causH: .6,
    falloff: 22, pickLo: .05, pickHi: .55,
    whiteA: .38, inkA: .8, radius: 10, shadow: 1, navBlur: 18, navA1: .74, navA2: .58, navSat: 190, navEdge: 9, navEdgeBlur: 3, navStrip: .85,
    L_refr: 1, L_body: 1, L_refl: 1, L_glow: 1, L_caus: 1, L_wall: 1, L_crest: 1, L_key: 1, L_hover: 1, L_shadow: 1, L_navFrost: 1, L_navStrip: 1
  };
  function P(n) { var o = W.GLASS_P; return o && o[n] != null && isFinite(+o[n]) ? +o[n] : PD[n]; }
  function clamp(x, a, b) { return x < a ? a : x > b ? b : x; }
  function sstep(a, b, x) { x = clamp((x - a) / (b - a), 0, 1); return x * x * (3 - 2 * x); }
  function intensity() { var g = W.GLASS_INTENSITY; return g == null || !isFinite(+g) ? 1 : clamp(+g, 0, 3); }

  /* ---------- colour ---------- */
  function parse(s) {
    if (!s || s === 'none' || s === 'transparent') return null;
    if (colCache[s]) return colCache[s];
    var m = /^rgba?\(([\d.]+)[, ]+([\d.]+)[, ]+([\d.]+)(?:[,/ ]+([\d.]+%?))?\)$/.exec(s), c;
    if (m) c = [+m[1], +m[2], +m[3], m[4] == null ? 1 : parseFloat(m[4]) / (/%/.test(m[4]) ? 100 : 1)];
    else {
      probe.clearRect(0, 0, 1, 1); probe.fillStyle = '#000'; probe.fillStyle = s; probe.fillRect(0, 0, 1, 1);
      var d = probe.getImageData(0, 0, 1, 1).data; c = d[3] ? [d[0] * 255 / d[3], d[1] * 255 / d[3], d[2] * 255 / d[3], d[3] / 255] : null;
    }
    return (colCache[s] = c);
  }
  function hex(h) { h = String(h || '').trim().replace('#', ''); if (h.length === 3) h = h.replace(/./g, '$&$&'); var n = parseInt(h, 16); return h.length === 6 && !isNaN(n) ? [n >> 16 & 255, n >> 8 & 255, n & 255] : null; }
  function toHex(c) { return '#' + c.map(function (v) { return ('0' + Math.round(clamp(v, 0, 255)).toString(16)).slice(-2); }).join(''); }
  function mix(a, b, k) { return [a[0] + (b[0] - a[0]) * k, a[1] + (b[1] - a[1]) * k, a[2] + (b[2] - a[2]) * k]; }
  function lin(v) { v /= 255; return v <= .04045 ? v / 12.92 : Math.pow((v + .055) / 1.055, 2.4); }
  function lum(c) { return .2126 * lin(c[0]) + .7152 * lin(c[1]) + .0722 * lin(c[2]); }
  function contrast(a, b) { var x = lum(a) + .05, y = lum(b) + .05; return x > y ? x / y : y / x; }
  function rgba(c, a) { return 'rgba(' + (c[0] | 0) + ',' + (c[1] | 0) + ',' + (c[2] | 0) + ',' + clamp(a, 0, 1).toFixed(3) + ')'; }
  // OKLab: perceptual, so "how colourful" and "how different" mean what they look like
  function toLab(c) {
    var r = lin(c[0]), g = lin(c[1]), b = lin(c[2]);
    var l = Math.cbrt(.4122214708 * r + .5363325363 * g + .0514459929 * b), m = Math.cbrt(.2119034982 * r + .6806995451 * g + .1073969566 * b), s = Math.cbrt(.0883024619 * r + .2817188376 * g + .6299787005 * b);
    return [.2104542553 * l + .793617785 * m - .0040720468 * s, 1.9779984951 * l - 2.428592205 * m + .4505937099 * s, .0259040371 * l + .7827717662 * m - .808675766 * s];
  }
  function fromLab(L) {
    var l = L[0] + .3963377774 * L[1] + .2158037573 * L[2], m = L[0] - .1055613458 * L[1] - .0638541728 * L[2], s = L[0] - .0894841775 * L[1] - 1.291485548 * L[2];
    l = l * l * l; m = m * m * m; s = s * s * s;
    return [4.0767416621 * l - 3.3077115913 * m + .2309699292 * s, -1.2684380046 * l + 2.6097574011 * m - .3413193965 * s, -.0041960863 * l - .7034186147 * m + 1.707614701 * s]
      .map(function (v) { v = clamp(v, 0, 1); return 255 * (v <= .0031308 ? 12.92 * v : 1.055 * Math.pow(v, 1 / 2.4) - .055); });
  }
  /* How much a colour seen near the button should register on it.
     - colourfulness: OKLCH chroma. Grey, white and black are ~0, so they never tint the glass.
     - contrast with the button: distance in the colour (a,b) plane from the button's own surface,
       so a yellow note does not tint a yellow button, plus a small boost when lightness differs.
     A black button next to a white one therefore scores 0: the old engine scored it ~0.9. */
  function salience(lab, surf) {
    // round 5: pale washes count, in proportion (C .05 ~ half weight); greys (C < .012) still never tint
    var C = Math.sqrt(lab[1] * lab[1] + lab[2] * lab[2]), wc = sstep(P('paleLo'), Math.max(P('paleLo') + .01, P('paleHi')), C);
    if (!wc) return 0;
    var dab = Math.sqrt((lab[1] - surf[1]) * (lab[1] - surf[1]) + (lab[2] - surf[2]) * (lab[2] - surf[2]));
    return wc * sstep(.02, .13, dab) * (.65 + .35 * sstep(0, .35, Math.abs(lab[0] - surf[0])));
  }
  // The colour glass shows for a reflection: the source's hue, its lightness pulled to suit the glass, chroma
  // lifted (glass edges concentrate colour, so a pastel wash shows as a clearer line of its hue) and capped
  function reflectColour(a, b, L, Cmax) { var C = Math.sqrt(a * a + b * b) || 1e-6, k = Math.min(Cmax, C * P('boost')) / C; return fromLab([L, a * k, b * k]); }
  // Soft knee: linear for small values, never above cap
  function knee(x, cap) { return cap * (1 - Math.exp(-x / cap)); }

  /* ---------- reading the page (read-only) ---------- */
  function pixel(el, x, y) {
    var key = el.tagName === 'IMG' ? el.currentSrc || el.src : 'c', now = Date.now(), e = pxCache.get(el);
    if (!e || e.key !== key || (key === 'c' && now - e.t > 500)) {
      e = { key: key, t: now, d: null };
      if (el.tagName !== 'IMG' || (el.complete && el.naturalWidth)) {
        try { var c = D.createElement('canvas'); c.width = c.height = 24; var x2 = c.getContext('2d', { willReadFrequently: true }); x2.drawImage(el, 0, 0, 24, 24); e.d = x2.getImageData(0, 0, 24, 24).data; }
        catch (err) { e.d = null; } // tainted: fall back to the element's own colour
      }
      pxCache.set(el, e);
    }
    if (!e.d) return null;
    var r = el.getBoundingClientRect(), u = clamp(((x - r.left) / r.width * 24) | 0, 0, 23), v = clamp(((y - r.top) / r.height * 24) | 0, 0, 23), i = (v * 24 + u) * 4;
    return e.d[i + 3] > 128 ? [e.d[i], e.d[i + 1], e.d[i + 2]] : null;
  }
  function gradAvg(s) {
    var m = s.match(/(rgba?\([^)]*\)|#[0-9a-f]{3,8}\b|(?:oklch|oklab|hsla?|color)\([^)]*\))/gi); if (!m) return null;
    var t = [0, 0, 0], n = 0; m.forEach(function (x) { var c = parse(x); if (c && c[3] > .05) { t[0] += c[0]; t[1] += c[1]; t[2] += c[2]; n++; } });
    return n ? [t[0] / n, t[1] / n, t[2] / n] : null;
  }
  /* Gradients read where the sample lands (round 5). Supports what the site uses: radial-gradient with
     "W% H% at X% Y%" (or the default ellipse), linear-gradient with deg / "to side", colour stops in %.
     url() layers (the grain) are skipped. Anything else falls back to the old average. */
  function splitTop(s) {
    var out = [], d = 0, st = 0, q = '';
    for (var i = 0; i < s.length; i++) {
      var ch = s[i];
      if (q) { if (ch === q) q = ''; continue; }
      if (ch === '"' || ch === "'") q = ch; else if (ch === '(') d++; else if (ch === ')') d--;
      else if (ch === ',' && !d) { out.push(s.slice(st, i).trim()); st = i + 1; }
    }
    out.push(s.slice(st).trim()); return out;
  }
  var gradCache = {}, SIDES = { top: 0, right: 90, bottom: 180, left: 270, 'top right': 45, 'right top': 45, 'bottom right': 135, 'right bottom': 135, 'bottom left': 225, 'left bottom': 225, 'top left': 315, 'left top': 315 };
  function parseGrad(layer) {
    if (gradCache.hasOwnProperty(layer)) return gradCache[layer];
    var g = null, m = /^(repeating-)?(radial|linear)-gradient\(([\s\S]*)\)$/i.exec(layer), i;
    if (/^url\(/i.test(layer) || layer === 'none') g = 'skip';
    else if (m && !m[1]) {
      var parts = splitTop(m[3]), head = parts[0], stops = parts, kind = m[2].toLowerCase();
      g = { kind: kind };
      if (kind === 'radial') {
        var h = /^(?:ellipse\s*)?(?:(-?[\d.]+)%\s+(-?[\d.]+)%)?\s*(?:at\s+(-?[\d.]+)%\s+(-?[\d.]+)%)?$/i.exec(head);
        if (h && (h[1] || h[3] || /^ellipse$/i.test(head))) { g.rx = h[1] ? +h[1] / 100 : null; g.ry = h[2] ? +h[2] / 100 : null; g.cx = h[3] ? +h[3] / 100 : .5; g.cy = h[4] ? +h[4] / 100 : .5; stops = parts.slice(1); }
        else if (/^(circle|ellipse|closest|farthest|at\s)/i.test(head)) g = null;
        else { g.rx = null; g.cx = g.cy = .5; }
      } else {
        var a = /^(-?[\d.]+)deg$/i.exec(head), t = /^to\s+(.+)$/i.exec(head);
        if (a) { g.ang = +a[1]; stops = parts.slice(1); }
        else if (t) { g.ang = SIDES[t[1].trim().toLowerCase()]; stops = parts.slice(1); if (g.ang == null) g = null; }
        else g.ang = 180;
      }
      if (g) {
        var list = [];
        for (i = 0; i < stops.length; i++) {
          var sm = /^([\s\S]*?)(?:\s+(-?[\d.]+)%)?$/.exec(stops[i]), c = sm && parse(sm[1].trim());
          if (!c) { g = null; break; }
          list.push({ c: c, p: sm[2] != null ? +sm[2] / 100 : null });
        }
        if (g && list.length > 1) {
          if (list[0].p == null) list[0].p = 0;
          if (list[list.length - 1].p == null) list[list.length - 1].p = 1;
          for (i = 1; i < list.length; i++) if (list[i].p == null) {
            var j = i; while (list[j].p == null) j++;
            for (var k = i; k < j; k++) list[k].p = list[i - 1].p + (list[j].p - list[i - 1].p) * (k - i + 1) / (j - i + 1);
            i = j;
          }
          g.stops = list;
        } else g = null;
      }
    }
    return (gradCache[layer] = g);
  }
  function stopAt(st, t) {
    if (t <= st[0].p) return st[0].c;
    for (var i = 1; i < st.length; i++) if (t <= st[i].p) {
      var a = st[i - 1].c, b = st[i].c, k = st[i].p > st[i - 1].p ? (t - st[i - 1].p) / (st[i].p - st[i - 1].p) : 1;
      return [a[0] + (b[0] - a[0]) * k, a[1] + (b[1] - a[1]) * k, a[2] + (b[2] - a[2]) * k, a[3] + (b[3] - a[3]) * k];
    }
    return st[st.length - 1].c;
  }
  function gradAt(g, px, py, w, h) {
    var t;
    if (g.kind === 'radial') {
      var cx = g.cx * w, cy = g.cy * h, rx, ry;
      if (g.rx != null) { rx = g.rx * w; ry = g.ry * h; }
      else { rx = Math.max(cx, w - cx) * Math.SQRT2; ry = Math.max(cy, h - cy) * Math.SQRT2; }
      var u = (px - cx) / (rx || 1), v = (py - cy) / (ry || 1); t = Math.sqrt(u * u + v * v);
    } else {
      var an = g.ang * Math.PI / 180, sx = Math.sin(an), sy = -Math.cos(an), L = Math.abs(w * sx) + Math.abs(h * sy) || 1;
      t = ((px - w / 2) * sx + (py - h / 2) * sy) / L + .5;
    }
    return stopAt(g.stops, t);
  }
  // an element's own background (gradients over its colour) at a viewport point: [r,g,b,a], or undefined
  function bgAt(el, cs, x, y) {
    var bi = cs.backgroundImage; if (!bi || bi === 'none') return undefined;
    var layers = splitTop(bi), gs = [], i;
    for (i = 0; i < layers.length; i++) { var g = parseGrad(layers[i]); if (!g) return undefined; if (g !== 'skip') gs.push(g); }
    if (!gs.length) return undefined;
    var R = el.getBoundingClientRect(), px = x - R.left, py = y - R.top, bg = parse(cs.backgroundColor);
    var c = bg && bg[3] > .01 ? bg.slice(0, 3) : [0, 0, 0], a = bg && bg[3] > .01 ? bg[3] : 0;
    for (i = gs.length - 1; i >= 0; i--) { // bottom layer first
      var s = gradAt(gs[i], px, py, R.width, R.height), sa = clamp(s[3], 0, 1), oa = sa + a * (1 - sa);
      if (oa > 1e-4) c = [(s[0] * sa + c[0] * a * (1 - sa)) / oa, (s[1] * sa + c[1] * a * (1 - sa)) / oa, (s[2] * sa + c[2] * a * (1 - sa)) / oa];
      a = oa;
    }
    return c.concat(a);
  }
  function glassOf(el) { return el && el.closest ? el.closest('[data-glass-kind]') : null; }
  function colorOf(el, x, y, memo) {
    if (!el) return null;
    if (memo.has(el)) return memo.get(el);
    var c = null, own = true, cs = getComputedStyle(el), tag = el.tagName, g = glassOf(el);
    var dc = el.getAttribute && hex(el.getAttribute('data-color'));
    if (dc) c = dc;
    else if (g && g.getAttribute('data-glass-kind') === 'solid') c = hex(g.getAttribute('data-glass-fill'));
    else if (tag === 'IMG' || tag === 'CANVAS' || tag === 'VIDEO') { c = pixel(el, x, y); own = false; }
    else if (W.SVGGraphicsElement && el instanceof SVGGraphicsElement && tag.toLowerCase() !== 'svg') { var f = parse(cs.fill); if (f && f[3] > .1) c = mix(colorOf(el.parentNode, x, y, memo) || WHITE, f, f[3] * (+cs.opacity || 1)); own = false; }
    if (!c) {
      var bi = cs.backgroundImage, bg = parse(cs.backgroundColor), gp = bgAt(el, cs, x, y), ga = gp === undefined && bi && bi !== 'none' && gradAvg(bi);
      if (gp) { var gc = gp.slice(0, 3); c = gp[3] > .97 ? gc : mix(colorOf(el.parentElement, x, y, memo) || WHITE, gc, gp[3]); own = false; } // positional: never memoised
      else if (ga) c = bg && bg[3] > .5 ? mix(bg, ga, .7) : ga;
      else if (bg && bg[3] > .03) c = bg[3] > .97 ? bg.slice(0, 3) : mix(colorOf(el.parentElement, x, y, memo) || WHITE, bg, bg[3]);
      else if (el === D.documentElement) c = WHITE;
      else { c = colorOf(el.parentElement, x, y, memo) || WHITE; own = false; }
      var fs = parseFloat(cs.fontSize), tc = fs >= 20 && parse(cs.color);
      if (tc && [].some.call(el.childNodes, function (n) { return n.nodeType === 3 && n.data.trim(); })) c = mix(c, tc, Math.min(.6, fs / 60));
    }
    if (own) memo.set(el, c);
    return c;
  }
  function isClear(el) { var g = glassOf(el); return g && g.getAttribute('data-glass-kind') === 'clear'; }
  // does this element paint anything itself? Transparent wrappers (a toolbar row, a flex container) do not,
  // so the hit test looks through them to what is visually underneath instead of walking up the DOM.
  function paints(el) {
    if (el === D.documentElement || el === D.body) return true;
    var tag = el.tagName;
    if (tag === 'IMG' || tag === 'CANVAS' || tag === 'VIDEO' || el.hasAttribute('data-color')) return true;
    var g = glassOf(el); if (g && g.getAttribute('data-glass-kind') === 'solid') return true;
    if (W.SVGGraphicsElement && el instanceof SVGGraphicsElement && tag.toLowerCase() !== 'svg') return true;
    var cs = getComputedStyle(el), bg = parse(cs.backgroundColor);
    if ((bg && bg[3] > .03) || (cs.backgroundImage && cs.backgroundImage !== 'none')) return true;
    return parseFloat(cs.fontSize) >= 20 && [].some.call(el.childNodes, function (n) { return n.nodeType === 3 && n.data.trim(); });
  }
  function hitAt(x, y, self) {
    // fast path (P2-5): the top element, then its painting ancestor; the full stack only when the top layer is
    // invisible (a faded-out scene), the button itself or clear glass
    var top = D.elementFromPoint(x, y);
    if (top && !self.contains(top) && !isClear(top) && shown(top)) { for (var e0 = top; e0; e0 = e0.parentElement) { if (e0 === self) break; if (paints(e0)) return e0; } }
    var st = D.elementsFromPoint(x, y);
    for (var i = 0; i < st.length; i++) { var e = st[i]; if (self.contains(e) || isClear(e)) continue; if (paints(e) && shown(e)) return e; }
    return null;
  }
  function colorAt(x, y, self, memo) {
    if (x < 0 || y < 0 || x >= W.innerWidth || y >= W.innerHeight) return null;
    return colorOf(hitAt(x, y, self), x, y, memo) || WHITE;
  }
  // large text right behind the label: glyph edges stay sharp through clear glass, so it gets more frost
  function textBehind(el) {
    if (!el || el.tagName === 'IMG' || el.tagName === 'CANVAS' || (W.SVGElement && el instanceof SVGElement)) return false;
    var cs = getComputedStyle(el), bg = parse(cs.backgroundColor);
    return !(bg && bg[3] > .03) && parseFloat(cs.fontSize) >= 20;
  }

  /* ---------- rounded-rect geometry ---------- */
  function rrPts(x, y, w, h, r, n) {
    r = Math.max(0, Math.min(r, w / 2, h / 2));
    var a = w - 2 * r, b = h - 2 * r, q = Math.PI * r / 2, L = 2 * a + 2 * b + 4 * q, out = [], H = Math.PI / 2;
    function arc(cx, cy, t) { out.push({ x: cx + r * Math.cos(t), y: cy + r * Math.sin(t), nx: Math.cos(t), ny: Math.sin(t) }); }
    for (var i = 0; i < n; i++) {
      var s = i * L / n;
      if (s < a) out.push({ x: x + r + s, y: y, nx: 0, ny: -1 });
      else if ((s -= a) < q) arc(x + w - r, y + r, -H + s / Math.max(r, 1e-6));
      else if ((s -= q) < b) out.push({ x: x + w, y: y + r + s, nx: 1, ny: 0 });
      else if ((s -= b) < q) arc(x + w - r, y + h - r, s / Math.max(r, 1e-6));
      else if ((s -= q) < a) out.push({ x: x + w - r - s, y: y + h, nx: 0, ny: 1 });
      else if ((s -= a) < q) arc(x + r, y + h - r, H + s / Math.max(r, 1e-6));
      else if ((s -= q) < b) out.push({ x: x, y: y + h - r - s, nx: -1, ny: 0 });
      else { s -= b; arc(x + r, y + r, Math.PI + s / Math.max(r, 1e-6)); }
    }
    return out;
  }
  function rrPath(ctx, x, y, w, h, r) {
    r = Math.max(0, Math.min(r, w / 2, h / 2)); ctx.beginPath(); ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r); ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath();
  }
  // signed geometry of a point inside a rounded rect: depth from the edge and the outward normal
  function rrDepth(x, y, w, h, r) {
    var px = x - w / 2, py = y - h / 2, sx = px < 0 ? -1 : 1, sy = py < 0 ? -1 : 1, qx = Math.abs(px) - (w / 2 - r), qy = Math.abs(py) - (h / 2 - r);
    if (qx > 0 && qy > 0) { var l = Math.sqrt(qx * qx + qy * qy) || 1; return [r - l, sx * qx / l, sy * qy / l]; }
    return qx > qy ? [r - qx, sx, 0] : [r - qy, 0, sy];
  }

  /* ---------- sampling the surroundings ---------- */
  // Rings of hit-test points hugging the button shape. Each sample keeps its OKLab colour and salience.
  // Performance (25 Oct): fewer, wider-spaced samples, and hit tests cached per refresh in a 4px grid, so
  // buttons near each other share them (elementsFromPoint is slow in deep, layered pages such as the hero).
  var RINGS = [4, 14, 30, 56, 96, 150, 210], HIT = new Map(), RUNMEMO = new Map(), SHOWN = new Map();
  function cachedColorAt(x, y, btn) {
    var k = (x >> 2) + ',' + (y >> 2);
    if (HIT.has(k)) return HIT.get(k);
    var c = colorAt(x, y, btn, RUNMEMO); HIT.set(k, c); return c;
  }
  // R: the on-screen box (may be scaled or rotated by a transform); w, h: the button's own layout size
  function sampleAround(btn, R, r, surf, w, h) {
    var kx = w / (R.width || w), ky = h / (R.height || h), rw = R.width, rh = R.height, rs = r / kx;
    var reach = Math.min(Math.max(2.2 * rw, 170), Math.max(60, P('reach'))) - rw / 2, out = [];
    RINGS.forEach(function (off) {
      if (off > reach) return;
      var small = rh < 34 || rh < h * .92, W2 = rw + 2 * off, H2 = rh + 2 * off, n = clamp(Math.round((2 * W2 + 2 * H2) / (20 + off * .7)), small ? 8 : 10, small ? 12 : 18);
      if (small && off > 40) return; // buttons drawn small or inside a scaled scene: about 30 near samples, not 100
      rrPts(R.left - off, R.top - off, W2, H2, rs + off, n).forEach(function (p) {
        var c = cachedColorAt(p.x, p.y, btn); if (!c) return;
        var lab = toLab(c);
        // hp (round 5): how much hue this sample has at all, for the light that comes through the glass.
        // Unlike salience it ignores the button's own colour and counts pale washes (C .03 is already ~.8)
        var C = Math.sqrt(lab[1] * lab[1] + lab[2] * lab[2]);
        out.push({ x: (p.x - R.left) * kx, y: (p.y - R.top) * ky, off: off, lab: lab, s: salience(lab, surf), hp: sstep(.005, .032, C) });
      });
    });
    return { samples: out, reach: reach };
  }
  /* Light arriving at each rim point.
     weight f = facing^1.3 / (1 + (d/d0)^2), faded to 0 at the reach. Every sample adds f to the total,
     coloured ones add f*salience to the colour, so intensity = salient share of what the rim point "sees".
     That one ratio gives all three behaviours: distance (near samples dominate), coverage (a small chip
     among white page is a small share) and colourfulness (neutrals add weight but no colour). */
  function lightRim(pts, S, d0) {
    var n = pts.length, I = new Float32Array(n), A = new Float32Array(n), B = new Float32Array(n), Ls = new Float32Array(n), sm = S.samples, far = S.reach + 30, i, j;
    var EI = new Float32Array(n), EA = new Float32Array(n), EB = new Float32Array(n);
    for (i = 0; i < n; i++) {
      var p = pts[i], F = 0, FS = 0, a = 0, b = 0, l = 0, FE = 0, ea = 0, eb = 0;
      for (j = 0; j < sm.length; j++) {
        var s = sm[j], vx = s.x - p.x, vy = s.y - p.y, d = Math.sqrt(vx * vx + vy * vy) || 1, cos = (vx * p.nx + vy * p.ny) / d;
        if (cos <= 0) continue;
        var q = d / d0, f = Math.pow(cos, 1.3) / (1 + q * q) * (1 - Math.pow(Math.min(1, d / far), 3));
        F += f;
        if (s.s > 0) { var fs = f * s.s; FS += fs; a += s.lab[1] * fs; b += s.lab[2] * fs; l += s.lab[0] * fs; }
        if (s.hp > 0) { var fe = f * s.hp, Cs = Math.sqrt(s.lab[1] * s.lab[1] + s.lab[2] * s.lab[2]) || 1; FE += fe; ea += s.lab[1] / Cs * fe; eb += s.lab[2] / Cs * fe; } // hue direction only
      }
      I[i] = F > 1e-6 ? FS / F : 0;
      if (FS > 1e-6) { A[i] = a / FS; B[i] = b / FS; Ls[i] = l / FS; }
      EI[i] = F > 1e-6 ? FE / F : 0;
      if (FE > 1e-6) { var eh = Math.sqrt(ea * ea + eb * eb) / FE; EA[i] = ea / FE; EB[i] = eb / FE; EI[i] *= sstep(.15, .6, eh); } // opposite hues cancel: no mud
    }
    // soften along the rim (colour and intensity), weighting colour by intensity so dark gaps do not grey it
    function smooth(arr, wt) {
      var o = new Float32Array(n), K = [1, 2, 3, 2, 1];
      for (var i = 0; i < n; i++) { var t = 0, tw = 0; for (var k = -2; k <= 2; k++) { var m = (i + k + n) % n, ww = K[k + 2] * (wt ? wt[m] + 1e-4 : 1); t += arr[m] * ww; tw += ww; } o[i] = t / tw; }
      return o;
    }
    A = smooth(A, I); B = smooth(B, I); Ls = smooth(Ls, I); I = smooth(I);
    // the light through the glass is spread wider along the rim (3 passes): corners see two sides at once and
    // otherwise collect a bright spot
    for (var pass = 0; pass < 3; pass++) { EA = smooth(EA, EI); EB = smooth(EB, EI); EI = smooth(EI); }
    // ambient = what surrounds the button on all sides (it sits on a colour card). It is taken out of the
    // rim so uniform surroundings never draw an outline; variants may show it as a faint body tint instead.
    var sorted = Array.prototype.slice.call(I).sort(function (x, y) { return x - y; }), amb = sorted[Math.floor(n * .2)] || 0;
    var ta = 0, tb = 0, tw = 0, peak = 0, pk = 0;
    for (i = 0; i < n; i++) { ta += A[i] * I[i]; tb += B[i] * I[i]; tw += I[i]; if (I[i] > peak) { peak = I[i]; pk = i; } }
    return { I: I, A: A, B: B, L: Ls, EI: EI, EA: EA, EB: EB, amb: amb, ambA: tw ? ta / tw : 0, ambB: tw ? tb / tw : 0, peak: peak, peakAt: pk };
  }
  /* Turn rim light into drawable alpha with the clamps:
     - directional part only (ambient removed)
     - soft knee to `cap`
     - total budget: if the lit share of the rim gets large, everything is scaled down, so the rim can
       never become a full coloured outline. */
  function rimAlpha(Lr, gain, cap, budget) {
    var n = Lr.I.length, out = new Float32Array(n), sum = 0, i;
    // round 5: .55 of the ambient is taken out (was .9), so a button sitting on a wash still shows its hue at the lip
    for (i = 0; i < n; i++) { out[i] = knee(Math.max(0, Lr.I[i] - P('ambOut') * Lr.amb) * gain * P('refl'), cap); sum += out[i]; }
    var mean = sum / n;
    if (mean > budget) for (i = 0; i < n; i++) out[i] *= budget / mean;
    return out;
  }

  /* ---------- per-pixel edge band ---------- */
  function buildBand(w, h, r, dpr, rim, depth) {
    var CW = Math.round(w * dpr), CH = Math.round(h * dpr), ix = [], ri = [], dd = [], nx = [], ny = [], n = rim.length;
    for (var Y = 0; Y < CH; Y++) for (var X = 0; X < CW; X++) {
      var x = (X + .5) / dpr, y = (Y + .5) / dpr, g = rrDepth(x, y, w, h, r);
      if (g[0] > depth || g[0] * dpr < -.5) continue;
      var best = 0, bd = 1e9;
      for (var i = 0; i < n; i++) { var dx = rim[i].x - x, dy = rim[i].y - y, d = dx * dx + dy * dy; if (d < bd) { bd = d; best = i; } }
      ix.push((Y * CW + X) * 4); ri.push(best); dd.push(g[0]); nx.push(g[1]); ny.push(g[2]);
    }
    return { ix: ix, ri: ri, d: dd, nx: nx, ny: ny, CW: CW, CH: CH };
  }

  /* ---------- refraction maps ---------- */
  var mapCache = {}, defs = null, uid = 0;
  // profile(d) -> 0..1 displacement magnitude at depth d px from the edge; encoded R/G = 128 +- 127, pointing inward
  // mag > 0 adds a lens term: every pixel samples a little toward the centre, so the middle magnifies
  function dispMap(w, h, r, key, profile, mag) {
    mag = mag || 0;
    var k = w + 'x' + h + 'r' + r + key + 'm' + mag; if (mapCache[k]) return mapCache[k];
    var c = D.createElement('canvas'); c.width = w; c.height = h;
    var x2 = c.getContext('2d'), im = x2.createImageData(w, h), d = im.data;
    for (var y = 0; y < h; y++) for (var x = 0; x < w; x++) {
      var g = rrDepth(x + .5, y + .5, w, h, r), m = profile(Math.max(0, g[0])), i = (y * w + x) * 4;
      var ux = (x + .5 - w / 2) / (w / 2), uy = (y + .5 - h / 2) / (h / 2);
      d[i] = clamp(128 - (g[1] * m + ux * mag) * 127, 0, 255); d[i + 1] = clamp(128 - (g[2] * m + uy * mag) * 127, 0, 255); d[i + 2] = 128; d[i + 3] = 255;
    }
    x2.putImageData(im, 0, 0);
    return (mapCache[k] = c.toDataURL());
  }
  function svgFilter(api, w, h, inner) {
    if (!defs) {
      var svg = D.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('aria-hidden', 'true'); svg.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden';
      defs = D.createElementNS(svg.namespaceURI, 'defs'); svg.appendChild(defs); D.body.appendChild(svg);
    }
    var f = api.filter;
    if (!f) { f = api.filter = D.createElementNS('http://www.w3.org/2000/svg', 'filter'); f.id = PFX + 'f' + (++uid); defs.appendChild(f); }
    f.setAttribute('x', 0); f.setAttribute('y', 0); f.setAttribute('width', w); f.setAttribute('height', h);
    f.setAttribute('filterUnits', 'userSpaceOnUse'); f.setAttribute('primitiveUnits', 'userSpaceOnUse'); f.setAttribute('color-interpolation-filters', 'sRGB');
    f.innerHTML = inner;
    return f.id;
  }
  /* Snell profile for a convex squircle lip (after kube.io's write-up): surface height z(t) = (1-(1-t)^4)^(1/4),
     t = depth / bezel. A vertical ray hits a surface tilted by a = atan(z'), bends to b = asin(sin a / n), and
     lands displaced by (glass height) * tan(a - b). Normalised so the edge is 1. */
  function snellProfile(bezel, n) {
    var N = 64, tab = new Float32Array(N + 1), mx = 0;
    for (var i = 0; i <= N; i++) {
      var t = Math.max(.004, i / N), u = 1 - t, base = 1 - u * u * u * u, z = Math.pow(base, .25), dz = Math.pow(base, -.75) * u * u * u;
      var a = Math.atan(dz * .55), b = Math.asin(Math.sin(a) / n), v = (z + .25) * Math.tan(a - b);
      tab[i] = v; if (v > mx) mx = v;
    }
    for (i = 0; i <= N; i++) tab[i] /= mx || 1;
    return function (d) { if (d >= bezel) return 0; var x = d / bezel * N, i0 = x | 0; return tab[i0] + (tab[Math.min(N, i0 + 1)] - tab[i0]) * (x - i0); };
  }

  /* ---------- strength: "subtle" (everyday buttons) or "liquid" (reference-level refraction) ---------- */
  function liquid() { return W.GLASS_MODE === 'liquid'; }
  function powerProfile(bez) { return function (d) { var t = Math.min(1, d / bez); return Math.pow(1 - t, 2.2); }; }
  /* Refraction, built the way liquid-glass-react does it: a warp layer inside the button carries the glass
     colour and a plain backdrop-filter (blur, saturate), and an SVG filter is applied to that layer with
     filter: url(). Putting url() inside backdrop-filter instead makes Chromium render an offset part of
     the box (visible as a hard-edged patch), so it is not used.
     o (subtle settings): { snell, bez, scale, split, post, fallback }. Liquid mode, after liquid-glass-react's
     defaults scaled to button height: a Snell lip ~45% of the height deep, displacement ~1.1x height
     (cap 70, their default), a lens magnification term, a chromatic split of +-10% (their aberration 2),
     a 1.5px frost and saturate 1.6. */
  function refractParts(api, B, w, h, r, o) {
    var L = liquid(), bez, prof, sc, split, mag, post;
    if (L) { bez = Math.max(8, Math.min(h / 2 - 1, h * .45)); prof = snellProfile(bez, 1.5); sc = clamp(h * 1.1, 28, 70); split = .1; mag = .16; post = 'blur(1.5px) saturate(1.6)'; }
    else { bez = o.bez; prof = o.snell ? snellProfile(bez, 1.5) : powerProfile(bez); sc = o.scale; split = o.split || 0; mag = 0; post = o.post; }
    var map = dispMap(w, h, Math.round(r), (L ? 'L' : o.snell ? 's' : 'p') + bez.toFixed(1), prof, mag);
    var inner = '<feImage href="' + map + '" x="0" y="0" width="' + w + '" height="' + h + '" preserveAspectRatio="none" result="m"/>';
    if (split) { // per-channel displacement, recombined by screen as liquid-glass-react does
      var ch = function (k, mtx, res) { return '<feDisplacementMap in="SourceGraphic" in2="m" scale="' + (sc * k).toFixed(1) + '" xChannelSelector="R" yChannelSelector="G" result="d' + res + '"/><feColorMatrix in="d' + res + '" type="matrix" values="' + mtx + '" result="' + res + '"/>'; };
      inner += ch(1 + split, '1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 1 0', 'cr') + ch(1, '0 0 0 0 0 0 1 0 0 0 0 0 0 0 0 0 0 0 1 0', 'cg') + ch(1 - split, '0 0 0 0 0 0 0 0 0 0 0 0 1 0 0 0 0 0 1 0', 'cb') +
        '<feBlend in="cg" in2="cb" mode="screen" result="gb"/><feBlend in="cr" in2="gb" mode="screen"/>';
    } else inner += '<feDisplacementMap in="SourceGraphic" in2="m" scale="' + sc.toFixed(1) + '" xChannelSelector="R" yChannelSelector="G"/>';
    if (api.el._busy) post = post.replace(/blur\(([\d.]+)px\)/, function (m, v) { return 'blur(' + Math.max(+v, 4) + 'px)'; });
    return { filter: 'url(#' + svgFilter(api, w, h, inner) + ')', backdrop: post.trim() + (B.tint ? B.bfx : '') };
  }
  // o: subtle settings, or null for a variant that only refracts in Liquid mode (V1)
  function applyRefract(api, B, btn, w, h, r, o) {
    var glassy = !B.prim || B.tint, use = REFRACT && glassy && (o || liquid()) && P('L_refr') > 0, wp = btn.querySelector(':scope>.' + PFX + '-warp'), bd = btn.querySelector(':scope>.' + PFX + '-body');
    if (!use) {
      btn.classList.remove(PFX + '-warped'); if (wp) wp.remove(); if (bd) bd.remove();
      var fb = glassy ? (liquid() ? 'blur(3px) saturate(1.6)' : o ? o.fallback : '') : '';
      if (fb && B.tint) fb += B.bfx; else if (!fb && B.tint) fb = 'blur(14px)' + B.bfx;
      btn.style.backdropFilter = btn.style.webkitBackdropFilter = fb;
      return;
    }
    if (!wp) {
      bd = D.createElement('span'); bd.className = PFX + '-body'; bd.setAttribute('aria-hidden', 'true'); btn.insertBefore(bd, btn.firstChild);
      wp = D.createElement('span'); wp.className = PFX + '-warp'; wp.setAttribute('aria-hidden', 'true'); btn.insertBefore(wp, bd);
    }
    var p = refractParts(api, B, w, h, r, o || {});
    wp.style.filter = p.filter; wp.style.backdropFilter = wp.style.webkitBackdropFilter = p.backdrop;
    btn.style.backdropFilter = btn.style.webkitBackdropFilter = '';
    btn.classList.add(PFX + '-warped');
  }
  /* Liquid mode, clear glass: pick the label the way vibrancy does. Either ink on thin white glass or white
     on thin smoked glass, whichever reaches AA with less opacity over what is behind. */
  function liquidLabel(B, worst) {
    if (!liquid() || B.prim || B.dark) return null;
    var a1 = glassAlpha(INK, worst, false, WHITE, .1), a2 = glassAlpha(WHITE, worst, true, [22, 22, 24], .1);
    return a1 <= a2 + .05 ? { fg: INK, gc: WHITE, a: a1 } : { fg: WHITE, gc: [22, 22, 24], a: a2 };
  }
  function applyMode(btn, lab) {
    btn.classList.toggle(PFX + '-liquid', liquid());
    if (lab) { btn.style.setProperty('--' + PFX + '-bg', rgba(lab.gc, lab.a)); btn.style.color = 'rgb(' + lab.fg.join(',') + ')'; }
    else { btn.style.removeProperty('--' + PFX + '-bg'); btn.style.color = ''; }
  }

  /* ---------- button setup ---------- */
  /* Fill colour: any CSS colour, including rgba() and 8-digit hex. Returns [r,g,b,a]. */
  function parseFill(v) { if (!v) return null; var h = hex(v); if (h) return h.concat(1); var c = parse(String(v).trim()); return c ? [c[0], c[1], c[2], c[3] == null ? 1 : c[3]] : null; }
  /* Label colour for a solid fill: white when the fill can reach 4.5:1 with a small darkening
     (at most 3 nudges, so the brand colour survives), otherwise ink. Then nudge the fill until AA holds. */
  function solidLabel(fill, force) {
    var fg = force, f = fill, i = 0;
    if (!fg) {
      var t = fill; for (i = 0; i <= 1 && contrast(WHITE, t) < 4.5; i++) t = mix(t, [0, 0, 0], .06);
      fg = contrast(WHITE, t) >= 4.5 ? WHITE : INK;
    }
    for (i = 0; i < 40 && contrast(fg, f) < 4.5; i++) f = mix(f, fg[0] > 128 ? [0, 0, 0] : WHITE, .03);
    return { fg: fg, fill: f };
  }
  var tintStyled = false;
  function setupButton(btn, o) {
    var variant = o.variant || btn.getAttribute(ATTR) || 'secondary';
    // in a dark theme (nearest [data-theme] is "dark") white glass would read as a grey, disabled slab: use the
    // clear dark-theme glass instead (light label, faint white fill, light hairline; see settingsCss)
    var th = btn.closest && btn.closest('[data-theme]');
    if (variant === 'secondary' && th && th.getAttribute('data-theme') === 'dark') { variant = 'dark'; btn.classList.add(PFX + '-ondark'); }
    if (!/^(primary|secondary|dark)$/.test(variant)) variant = 'secondary';
    var prim = variant === 'primary', dark = variant === 'dark';
    var fc = parseFill(o.fill || btn.getAttribute('data-fill'));
    // settings' inkA replaces the opacity of a translucent ink fill (solid app fills such as #1D1D1F stay solid)
    if (fc && fc[3] < 1 && lum(fc.slice(0, 3)) < .2 && W.GLASS_P && W.GLASS_P.inkA != null && isFinite(+W.GLASS_P.inkA)) fc[3] = +W.GLASS_P.inkA >= .995 ? 1 : +W.GLASS_P.inkA;
    var fa = fc ? clamp(fc[3], 0, 1) : 1;
    var fill = fc ? fc.slice(0, 3) : (dark ? [22, 22, 24] : prim ? INK : WHITE);
    var tint = prim && fa < .97, ff = btn.getAttribute('data-fg'), force = ff === 'white' ? WHITE : ff === 'ink' ? INK : null, fg;
    if (tint) fg = force || (lum(fill) < .35 ? WHITE : INK); // translucent: keep the colour, raise opacity for AA instead
    else if (prim) { var sl = solidLabel(fill, force); fg = sl.fg; fill = sl.fill; }
    else fg = dark ? [245, 245, 247] : INK;
    if (!tintStyled) {
      var st = D.createElement('style'), P = '.' + PFX;
      // liquid rim: a bright specular ring, strongest top-left and bottom-right, as on Apple's glass
      st.textContent = P + P + '-tint{--' + PFX + '-bg:rgba(var(--' + PFX + '-f),var(--' + PFX + '-a,.6))}' +
        P + P + '-warped{background:transparent;backdrop-filter:none;-webkit-backdrop-filter:none}' +
        // the warp carries only the backdrop (its own background would be lost by the filter);
        // the glass colour and inner ring sit on an unfiltered layer right above it
        P + '>' + P + '-warp,' + P + '>' + P + '-body{position:absolute;inset:0;z-index:0;display:block;border-radius:inherit;pointer-events:none}' +
        P + '>' + P + '-body{background:var(--' + PFX + '-bg);box-shadow:inset 0 0 0 .5px rgba(0,0,0,.12),inset 0 1px 0 rgba(255,255,255,.55)}' +
        P + P + '-dark>' + P + '-body,' + P + P + '-tint>' + P + '-body{box-shadow:inset 0 0 0 .5px rgba(255,255,255,.16),inset 0 1px 0 rgba(255,255,255,.12)}' +
        P + P + '-liquid{box-shadow:var(--' + PFX + '-sh),0 8px 24px rgba(0,0,0,.14)}' +
        P + P + '-liquid::before{content:"";position:absolute;inset:0;z-index:4;border-radius:inherit;padding:1.5px;pointer-events:none;' +
        'background:linear-gradient(135deg,rgba(255,255,255,.85) 0%,rgba(255,255,255,.25) 30%,rgba(255,255,255,.08) 55%,rgba(255,255,255,.6) 100%);' +
        '-webkit-mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0);-webkit-mask-composite:xor;mask-composite:exclude}';
      D.head.appendChild(st); tintStyled = true;
    }
    btn.classList.add(PFX, PFX + '-' + variant); if (tint) btn.classList.add(PFX + '-tint');
    btn.setAttribute('data-glass-kind', prim && !tint ? 'solid' : 'clear');
    if (prim) btn.setAttribute('data-glass-fill', toHex(fill));
    btn.style.setProperty('--' + PFX + '-f', fill.map(Math.round).join(','));
    btn.style.setProperty('--' + PFX + '-fg', 'rgb(' + fg.join(',') + ')');
    if (tint) btn.style.setProperty('--' + PFX + '-a', fa.toFixed(2));
    if (!btn.querySelector(':scope>span')) { var sp = D.createElement('span'); while (btn.firstChild) sp.appendChild(btn.firstChild); btn.appendChild(sp); }
    var cv = D.createElement('canvas'); btn.insertBefore(cv, btn.firstChild);
    // reflections are added light: on a solid fill they screen onto it, so blue + a yellow neighbour
    // brightens toward warm instead of averaging to grey-green. Not on translucent fills: a blended
    // child isolates the button, and the glass then sees an empty backdrop.
    if (prim && !tint) cv.style.mixBlendMode = 'screen';
    // translucent dark fills dim and saturate what shows through (like Apple's dark glass), so they can stay
    // see-through and still hold AA; light ones brighten it
    var dim = tint ? (fg[0] > 128 ? .6 : 1.12) : 1;
    var bfx = tint ? (dim !== 1 ? ' brightness(' + dim + ')' : '') + ' saturate(1.7)' : '';
    var flab = toLab(fill), colour = prim && Math.sqrt(flab[1] * flab[1] + flab[2] * flab[2]) > .05;
    return { variant: variant, prim: prim, dark: dark, tint: tint, fa: fa, fill: fill, flab: flab, colour: colour, dim: dim, bfx: bfx, fg: fg, cv: cv, ctx: cv.getContext('2d'), str: +(o.strength || btn.getAttribute('data-strength') || 1) };
  }
  // what the button's own surface looks like (for salience): fill, or fill over the backdrop
  function surfaceOf(B, base, kClear, kDark) { return B.prim ? (B.tint ? mix(base, B.fill, B.fa) : B.fill) : B.dark ? mix(base, B.fill, kDark) : mix(base, WHITE, kClear); }
  /* Reflection colour on this button. Neutral fills: the source hue at a fixed lightness.
     Coloured fills: keep the SOURCE hue at full-ish chroma, a little lighter than the fill. Mixing the two
     hues in Lab would pass through grey (blue + yellow = mud); a vivid thin edge over the fill reads as
     a warm catch instead. Same-hue neighbours were already scored near zero by salience(). */
  function reflectOn(B, a, b, L, Cmax) {
    if (!B.colour) return reflectColour(a, b, L, Cmax);
    var C = Math.sqrt(a * a + b * b) || 1e-6, Ct = clamp(C, .09, .17);
    return fromLab([clamp(B.flab[0] + .12, .62, .94), a / C * Ct, b / C * Ct]);
  }
  // glass opacity that keeps the label at 4.6:1 or better over the worst thing behind it
  function behind(btn, R, fg) {
    var memo = new Map(), base = null, worst = null; btn._busy = false;
    [.15, .5, .85].forEach(function (f) {
      var x = R.left + R.width * f, y = R.top + R.height / 2, c = colorAt(x, y, btn, memo) || WHITE;
      if (x >= 0 && y >= 0 && x < W.innerWidth && y < W.innerHeight && textBehind(hitAt(x, y, btn))) btn._busy = true;
      if (f === .5) base = c;
      if (!worst || contrast(fg, c) < contrast(fg, worst)) worst = c;
    });
    // inside a frosted bar (the glass nav) the button sits on frost, not on the raw page: AA is checked on that
    var fr = btn.closest && btn.closest('.site-nav,[data-glass-frost]');
    if (fr) { var fk = parseFloat(fr.getAttribute('data-glass-frost')) || .62; base = mix(base, WHITE, fk); worst = mix(worst, WHITE, fk); }
    return { base: base, worst: worst };
  }
  function glassAlpha(fg, worst, dark, fill, start, dim) {
    if (dim) worst = worst.map(function (v) { return clamp(v * dim, 0, 255); });
    var a = start; while (a < .94 && contrast(fg, mix(worst, dark ? fill : WHITE, a)) < 4.6) a += .03;
    return a;
  }
  function easeArr(cur, tgt, f) {
    var more = false;
    for (var i = 0; i < cur.length; i++) { var dv = tgt[i] - cur[i]; if (Math.abs(dv) > .0015) { cur[i] += dv * f; more = true; } else cur[i] = tgt[i]; }
    return more;
  }
  function reduced() { return !!W.GLASS_REDUCED || RM.matches; }
  /* hover (k), press (p) and pointer state + an rAF loop that only runs while something is easing.
     Hover comes from the pointer, :focus-visible or data-force; press from the pointer or Enter/Space.
     loop(s) returns true while the variant needs every frame (the shimmer). */
  function animator(btn, draw, ease, loop) {
    var s = { k: 0, goal: 0, p: 0, pgoal: 0, px: 0, py: 0, sx: 0, sy: 0, t: 0, hov: false, foc: false, down: false }, raf = 0, last = 0;
    function rest() {
      var b = btn.getBoundingClientRect(), fx = parseFloat(btn.getAttribute('data-px')), fy = parseFloat(btn.getAttribute('data-py'));
      s.px = b.width * (isFinite(fx) ? fx : .3); s.py = b.height * (isFinite(fy) ? fy : .25);
    }
    function goals() {
      var f = btn.getAttribute('data-force');
      s.goal = s.hov || s.foc || f === 'hover' || f === 'active' ? 1 : 0;
      s.pgoal = s.down || f === 'active' ? 1 : 0;
      if (f && !s.hov) rest();
    }
    function tick(now) {
      raf = 0;
      var dt = Math.min(48, last ? now - last : 16), more = false; last = now;
      goals();
      if (reduced()) { s.k = s.goal; s.p = s.pgoal; rest(); s.sx = s.px; s.sy = s.py; ease(1); draw(s); last = 0; return; }
      if (s.k !== s.goal) { s.k = s.goal > s.k ? Math.min(s.goal, s.k + dt / 240) : Math.max(s.goal, s.k - dt / 340); more = true; }
      if (s.p !== s.pgoal) { s.p = s.pgoal > s.p ? Math.min(1, s.p + dt / 90) : Math.max(0, s.p - dt / 220); more = true; }
      var f = 1 - Math.exp(-dt / Math.max(10, P('followSpeed')));
      if (Math.abs(s.px - s.sx) + Math.abs(s.py - s.sy) > .3) { s.sx += (s.px - s.sx) * f; s.sy += (s.py - s.sy) * f; more = true; }
      if (ease(f)) more = true;
      if (s.k > 0 && loop && loop(s)) { s.t += dt / 1000; more = true; }
      draw(s);
      if (more) raf = requestAnimationFrame(tick); else last = 0;
    }
    s.kick = function () { if (!raf) raf = requestAnimationFrame(tick); };
    s.rest = rest;
    function at(e) { if (reduced()) return rest(); var b = btn.getBoundingClientRect(); s.px = e.clientX - b.left; s.py = e.clientY - b.top; }
    btn.addEventListener('pointerenter', function (e) { if (btn.disabled) return; s.hov = true; at(e); if (!s.k) { s.sx = s.px; s.sy = s.py; } s.kick(); });
    btn.addEventListener('pointermove', function (e) { if (!s.hov) return; at(e); s.kick(); }, { passive: true });
    btn.addEventListener('pointerleave', function () { s.hov = false; s.down = false; s.kick(); });
    btn.addEventListener('pointerdown', function () { if (btn.disabled) return; s.down = true; s.kick(); });
    btn.addEventListener('pointerup', function () { s.down = false; s.kick(); });
    btn.addEventListener('pointercancel', function () { s.down = false; s.kick(); });
    // keyboard: the same hover on :focus-visible, the same press on Enter / Space
    btn.addEventListener('focus', function () { var fv = true; try { fv = btn.matches(':focus-visible'); } catch (e) {} if (!fv) return; s.foc = true; if (!s.hov) { rest(); if (!s.k) { s.sx = s.px; s.sy = s.py; } } s.kick(); });
    btn.addEventListener('blur', function () { s.foc = false; s.down = false; s.kick(); });
    btn.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { s.down = true; s.kick(); } });
    btn.addEventListener('keyup', function () { s.down = false; s.kick(); });
    return s;
  }

  /* ---------- batched, throttled refresh: every read, then every write ---------- */
  var all = [], pend = 0, timer = 0, lastRun = 0, GAP = 60, io = null, ro = null, force = true;
  // where a button is, relative to the page: unchanged on scroll for ordinary buttons (their surroundings scroll
  // with them), changed for buttons in a sticky nav or inside a moving scene
  /* When does a button need re-reading? (P2-5, 25 Oct)
     - its layout size or the settings changed, or it has just become visible (stacked scenes fade by opacity)
     - it sits in a sticky or fixed bar and the page scrolled (its surroundings changed)
     A button moved by an animation (the hero camera) is NOT re-read: its light stays as rendered for that scene. */
  function shown(el) {
    for (var e = el, i = 0; e && e !== D.body && i < 30; e = e.parentElement, i++) {
      var c = SHOWN.get(e); if (c === undefined) { var cs = getComputedStyle(e); c = cs.display === 'none' || cs.visibility === 'hidden' || +cs.opacity < .05 ? 0 : 1; if (cs.display === 'none') c = 0; SHOWN.set(e, c); }
      if (!c) return false;
    }
    return true;
  }
  function stuck(a) {
    if (a.stk == null) { a.stk = 0; for (var e = a.el.parentElement; e && e !== D.body; e = e.parentElement) { var p = getComputedStyle(e).position; if (p === 'sticky' || p === 'fixed') { a.stk = 1; break; } } }
    return a.stk;
  }
  // inView: a button can only read what is on screen, so one scanned while (partly) off screen is read again once it is
  // fully in view (this was missing on 25 Oct morning: buttons below the fold kept "no colour" until a resize)
  function posKey(a) { var R = a.el.getBoundingClientRect(), iv = R.top >= 0 && R.bottom <= W.innerHeight && R.left >= 0 && R.right <= W.innerWidth ? 1 : 0; return (a.el.offsetWidth | 0) + 'x' + (a.el.offsetHeight | 0) + ',' + (R.width ? 1 : 0) + ',' + iv + ',' + (W.GLASS_P_V || 0) + (stuck(a) ? ',' + Math.round(R.top + W.scrollY) : ''); }
  function run() {
    pend = 0; lastRun = performance.now(); settingsCss();
    var f = force; force = false; HIT.clear(); RUNMEMO = new Map(); SHOWN = new Map();
    var vis = all.filter(function (a) { if (!a.visible || !a.el.isConnected) return false; var sh = shown(a.el); if (!sh) { a.pk = 'hidden'; return false; } var k = posKey(a); if (!f && k === a.pk) return false; a.pk = k; return true; });
    // time-sliced (P2-5): scans run until ~10ms of this frame are used, the rest continue next frame, so a scene cut
    // that reveals several buttons never blocks the page
    queue = queue.filter(function (a) { return vis.indexOf(a) < 0; }).concat(vis);
    if (!slicing) slice();
  }
  var queue = [], slicing = 0;
  function slice() {
    slicing = 0;
    var t0 = performance.now(), done = [];
    while (queue.length && (performance.now() - t0 < 10 || !done.length)) { var a = queue.shift(); if (a.el.isConnected) { a.scan(); done.push(a); } }
    done.forEach(function (a) { a.write(); });
    if (queue.length) slicing = requestAnimationFrame(slice);
  }
  // buttons moved by an animation (no scroll or resize event): checked when the browser is idle, a few at a time
  var idleT = 0;
  function idleCheck() {
    idleT = 0;
    if (D.hidden) return;
    RUNMEMO = new Map(); SHOWN = new Map();
    var moved = all.filter(function (a) { return a.visible && a.el.isConnected && shown(a.el) && posKey(a) !== a.pk; }).slice(0, 2);
    if (moved.length) { HIT.clear(); moved.forEach(function (a) { a.pk = posKey(a); a.scan(); }); moved.forEach(function (a) { a.write(); }); }
  }
  setInterval(function () { if (!idleT && all.length) idleT = (W.requestIdleCallback || function (fn) { return setTimeout(fn, 1); })(idleCheck, { timeout: 600 }); }, 900);
  /* the page-level settings (radius, shadows, nav glass, layer switches) as one style element, from GLASS_P */
  var setEl = null, setTxt = '';
  var SHD = { // [x, y, blur, spread, alpha, inset, keep]  keep = the hairline edge, never scaled
    soft: { ink: [[0, 1, 2, 0, .16], [0, 4, 12, 0, .06]], white: [[0, 1, 2, 0, .05], [0, 4, 14, 0, .05], [0, 0, 0, .5, .1, 1]] },
    slab: { ink: [[0, 0, 0, .5, .2, 1, 1], [0, 1, 1, 0, .16], [0, 3, 6, -1, .14], [0, 12, 24, -12, .42]], white: [[0, 0, 0, .5, .1, 1, 1], [0, 1, 1, 0, .05], [0, 3, 6, -1, .06], [0, 12, 24, -12, .2]] }
  };
  function settingsCss() {
    var G = W.GLASS_P; if (!G) return;
    function sh(kind) { var on = P('L_shadow'), k = G.shadow != null ? +G.shadow : 1; return (SHD[G.shadowStyle] || SHD.slab)[kind].map(function (x) { var a = x[6] ? x[4] : x[4] * k * on; return (x[5] ? 'inset ' : '') + x[0] + 'px ' + x[1] + 'px ' + x[2] + 'px ' + x[3] + 'px rgba(0,0,0,' + a.toFixed(3) + ')'; }).join(','); }
    var rr = G.radius != null ? +G.radius : 10, r = rr >= 30 ? 999 : rr, fro = P('L_navFrost'), str = P('L_navStrip'), css = '';
    css += 'body ' + BTN + '.g3.lg{border-radius:' + r + 'px}body ' + BTN + '.g3.md{border-radius:' + (r >= 999 ? 999 : r * .9) + 'px}body ' + BTN + '.g3.sm{border-radius:' + (r >= 999 ? 999 : r * .8) + 'px}';
    css += 'body .g3-primary{--g3-sh:' + sh('ink') + '}body .g3-secondary{--g3-sh:' + sh('white') + '}';
    css += 'body .gbtn.g3{border:0;background:var(--g3-bg);color:var(--g3-fg)}';
    css += 'body .g3.g3-ondark{--g3-bg:rgba(255,255,255,.08)!important;--g3-fg:#E8EAED;color:#E8EAED;--g3-sh:inset 0 0 0 1px rgba(255,255,255,.14);--g3-ring:#E8EAED}' +
      'body .g3.g3-ondark>.g3-body{box-shadow:inset 0 0 0 1px rgba(255,255,255,.14),inset 0 1px 0 rgba(255,255,255,.12)!important}';
    // rim light (round 7): a thin bright ring, light top-left and bottom-right, darker on the sides, so clear glass
    // is legible as a button even on white; brightens a little on hover and focus. Plus a soft inner volume.
    function rim(sel, k) {
      if (!(k > 0)) return '';
      return sel.map(function (q) { return 'body ' + q + '::before'; }).join(',') + '{content:"";position:absolute;inset:0;z-index:3;border-radius:inherit;padding:1px;pointer-events:none;' +
        'background:linear-gradient(150deg,rgba(255,255,255,' + (.95 * k).toFixed(2) + ') 0%,rgba(255,255,255,' + (.25 * k).toFixed(2) + ') 30%,rgba(0,0,0,' + (.09 * k).toFixed(3) + ') 58%,rgba(255,255,255,' + (.6 * k).toFixed(2) + ') 100%);' +
        '-webkit-mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0);-webkit-mask-composite:xor;mask-composite:exclude;opacity:.65;transition:opacity .25s}' +
        sel.map(function (q) { return 'body ' + q + ':hover::before,body ' + q + ':focus-visible::before'; }).join(',') + '{opacity:1}';
    }
    css += rim(['.g3-secondary', '.gbtn.g3'], n('rimW', 0)) + rim(['.g3-primary'], n('rimI', 0));
    // frosted backing (go-live, 25 Oct): a light blur and milky fill behind the label of white glass, inset from the
    // edge so the clear refracting rim, rim light and colour pickup stay visible around it
    var fb = n('frostBlur', 0), fa = n('frostA', 0), fi = n('frostInset', 3);
    if (fb > 0 || fa > 0) {
      var fr = function (k) { return Math.max(0, (rr >= 30 ? 999 : rr * k) - fi); };
      css += 'body .g3-secondary:not(.g3-ondark)>.g3-body::before{content:"";position:absolute;inset:' + fi + 'px;border-radius:' + fr(1) + 'px;pointer-events:none;' +
        'background:linear-gradient(180deg,rgba(255,255,255,' + fa.toFixed(2) + '),rgba(255,255,255,' + (fa * .45).toFixed(2) + '));' +
        '-webkit-backdrop-filter:blur(' + fb + 'px) saturate(1.4);backdrop-filter:blur(' + fb + 'px) saturate(1.4)}' +
        'body ' + BTN + '.g3.md.g3-secondary>.g3-body::before{border-radius:' + fr(.9) + 'px}body ' + BTN + '.g3.sm.g3-secondary>.g3-body::before{border-radius:' + fr(.8) + 'px}';
    }
    var vol = n('volW', 0);
    if (vol > 0) {
      var vh = n('volH', 0); // hover: the inner volume deepens a little (reads on white glass over a white page, where added light cannot)
      var body = function (k) { return 'box-shadow:inset 0 0 0 .5px rgba(0,0,0,' + (.14 * vol).toFixed(3) + '),inset 0 1px 0 rgba(255,255,255,' + (.9 * vol).toFixed(2) + '),inset 0 ' + (-2 - 3 * k).toFixed(1) + 'px ' + (4 + 6 * k).toFixed(1) + 'px rgba(0,0,0,' + ((.05 + .06 * k) * vol).toFixed(3) + '),inset 0 10px 14px -10px rgba(255,255,255,' + (.85 * vol).toFixed(2) + ')'; };
      css += 'body .g3-secondary>.g3-body{' + body(0) + ';transition:box-shadow .25s}';
      if (vh > 0) css += 'body .g3-secondary:hover>.g3-body,body .g3-secondary:focus-visible>.g3-body,body .g3-secondary[data-force]>.g3-body{' + body(vh) + '}';
      css += '@media (prefers-reduced-motion:reduce){body .g3-secondary>.g3-body{transition:none}}';
    }
    css += '@media (prefers-reduced-motion:reduce){body .g3::before{transition:none}}';
    if (!P('L_body')) css += 'body .g3,body .g3>.g3-body{background:transparent!important}';
    function n(k, d) { return G[k] != null && isFinite(+G[k]) ? +G[k] : d; }
    css += '.site-nav{--nav-blur:' + (fro ? n('navBlur', 18) : 0) + 'px;--nav-a1:' + (fro ? n('navA1', .74) : 0) + ';--nav-a2:' + (fro ? n('navA2', .58) : 0) + ';--nav-sat:' + (fro ? n('navSat', 190) : 100) + '%;--nav-edge:' + (str ? n('navEdge', 9) : 0) + 'px;--nav-edge-blur:' + n('navEdgeBlur', 3) + 'px}';
    if (!str || !n('navEdge', 9)) css += '.site-nav::after{display:none}';
    if (!fro) css += '.site-nav::before{box-shadow:none}';
    if (css === setTxt && setEl && setEl.isConnected) return;
    setTxt = css; if (!setEl || !setEl.isConnected) { setEl = D.createElement('style'); setEl.id = 'g3-settings'; D.head.appendChild(setEl); }
    setEl.textContent = css;
  }
  settingsCss();
  var lastPub = 0;
  function refresh(now, soft) {
    if (now && !soft) force = true;
    if (pend) return;
    var wait = now ? 0 : GAP - (performance.now() - lastRun);
    if (wait <= 0) { clearTimeout(timer); timer = 0; pend = requestAnimationFrame(run); }
    else if (!timer) timer = setTimeout(function () { timer = 0; pend = requestAnimationFrame(run); }, wait);
  }
  function register(api) {
    all.push(api); api.visible = true;
    if (W.IntersectionObserver) {
      io = io || new IntersectionObserver(function (es) { es.forEach(function (e) { var a = e.target['_' + PFX]; if (a) a.visible = e.isIntersecting; }); refresh(); }, { rootMargin: '80px' });
      io.observe(api.el);
    }
    if (W.ResizeObserver) { ro = ro || new ResizeObserver(function () { refresh(); }); ro.observe(api.el); }
  }
  W.addEventListener('resize', function () { refresh(); }, { passive: true });
  W.addEventListener('scroll', function () { refresh(); }, { passive: true, capture: true });
  W.addEventListener('load', function () { refresh(true); });
  D.addEventListener('load', function (e) { if (e.target && e.target.tagName === 'IMG') refresh(); }, true);
  if (D.fonts && D.fonts.ready) D.fonts.ready.then(function () { refresh(true); });
  /* App mockups (v3 markup): a text .gbtn that sits next to a glass button (a header or modal footer pair such as
     "Preview as tester" + "Copy test link") becomes white glass at the same size, so the pair matches and the
     secondary picks up colour too. Icon-only (.ic) and stand-alone .gbtn stay plain. Off with appSecondary: 0. */
  function upgradeApp(root) {
    if (!W.GLASS_P || !+W.GLASS_P.appSecondary) return;
    [].forEach.call(root.querySelectorAll('.gbtn:not(.ic):not([' + ATTR + '])'), function (g) {
      var p = g.parentElement; if (!p || !p.querySelector(':scope>[' + ATTR + ']')) return;
      var big = p.querySelector(':scope>[' + ATTR + '].md') ? 'md' : p.querySelector(':scope>[' + ATTR + '].lg') ? 'lg' : 'sm';
      g.classList.add('btn', 'g3', big); g.setAttribute(ATTR, 'secondary');
    });
  }
  function boot(fn, name) {
    fn.init = function (root) { upgradeApp(root || D); var l = [].map.call((root || D).querySelectorAll('[' + ATTR + ']'), function (b) { return fn(b); }); refresh(true); return l; };
    fn.refresh = function () { var t = performance.now(); if (t - lastPub < 250) { refresh(); return; } lastPub = t; refresh(true, true); };
    fn.refraction = REFRACT;
    // APP ADDITION (figmaApp): forget a button React has unmounted (observers, refresh list, its SVG filter)
    fn.release = function (el) {
      if (!el) return;
      all = all.filter(function (a) { return a.el !== el; }); queue = queue.filter(function (a) { return a.el !== el; });
      if (io) io.unobserve(el); if (ro) ro.unobserve(el);
      var a = el._g3; if (a && a.filter && a.filter.parentNode) a.filter.parentNode.removeChild(a.filter);
      el._g3 = null;
    };
    W[name] = fn;
    if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', function () { fn.init(); }); else fn.init();
  }
  /* ===== end core ===== */

  /* ===== V3: From research =====
     A capsule of glass modelled the way kube.io and liquid-glass-react describe it:
     - refraction: a convex squircle lip, Snell's law (n = 1.5) gives the displacement per depth, baked into
       an feDisplacementMap used by backdrop-filter (Chromium; elsewhere a plain frost)
     - reflection: the environment is reflected only where the lip is steep, by a Schlick Fresnel term,
       so colour sits in a fine line right at the edge and nowhere else
     - specular: a fixed key light from the top left catches the lip (and faintly the opposite lip, as
       light passing through), plus a soft pointer light on hover */
  // Lab: no transform on hover or press. The button keeps its size and place; only the light changes.
  var CSSTXT = ':where(.g3){position:relative}.g3{display:inline-flex;align-items:center;justify-content:center;gap:6px;font:inherit;font-size:14px;font-weight:590;letter-spacing:-.005em;line-height:1.2;padding:10px 18px;border:0;border-radius:999px;color:var(--g3-fg);background:var(--g3-bg);box-shadow:var(--g3-sh),var(--g3-xs,0 0 0 transparent);cursor:pointer;white-space:nowrap;-webkit-tap-highlight-color:transparent}' +
    '.g3.g3-liquid{box-shadow:var(--g3-sh),0 8px 24px rgba(0,0,0,.14),var(--g3-xs,0 0 0 transparent)}' +
    '.g3:focus-visible{outline:2px solid var(--g3-ring);outline-offset:3px}.g3:disabled{opacity:.45;cursor:not-allowed}' +
    '.g3>canvas{position:absolute;inset:0;width:100%;height:100%;pointer-events:none;border-radius:inherit}.g3>span{position:relative;display:inline-flex;align-items:center;gap:6px}' +
    // round 5 slab: a hairline, a tight contact shadow under the wall, and a soft drop that starts below the button
    '.g3-primary{--g3-bg:rgb(var(--g3-f));--g3-sh:0 0 0 .5px rgba(0,0,0,.42),0 1px 1px rgba(0,0,0,.16),0 3px 6px -1px rgba(0,0,0,.14),0 12px 24px -12px rgba(0,0,0,.42);--g3-ring:rgb(var(--g3-f))}' +
    '.g3-secondary{--g3-bg:rgba(255,255,255,var(--g3-a,.38));--g3-sh:0 0 0 .5px rgba(0,0,0,.07),0 1px 1px rgba(0,0,0,.05),0 3px 6px -1px rgba(0,0,0,.06),0 12px 24px -12px rgba(0,0,0,.2);--g3-ring:#1D1D1F}' +
    '.g3-dark{--g3-bg:rgba(var(--g3-f),var(--g3-a,.28));--g3-sh:0 1px 2px rgba(0,0,0,.35),inset 0 0 0 .5px rgba(255,255,255,.14);--g3-ring:#F5F5F7}';
  var FLOOR_W = [142, 166, 206], FLOOR_I = [196, 210, 236]; // the neutral glass tone: cool steel, lighter on ink
  var styled = false, KEY = [-.42, -.91]; // key light: from the top left

  /* Hover recipes. Every value is a multiplier at full hover (k = 1); press (p) is applied on top.
     key: light direction follows the pointer      lift: thicker-looking glass (deeper bend, wider lip light, brighter rim)
     shimmer: caustic bands drift along the lip     press: a small lift on hover, the light flattens on press
     bright: brighter key light only                lens: a soft magnifying bulge in the glass follows the pointer */
  var HOVERS = {
    base: {}, key: { follow: 1, key: 1.1, caus: .8, glow: 1, spread: .5 },
    lift: { disp: 1.3, env: 1.5, key: 1, spread: 1, caus: 1.2, shadow: 1 },
    shimmer: { shim: 1, key: .35, caus: 1 },
    press: { disp: .5, env: .5, key: .5, spread: .4, caus: .4, shadow: .5 },
    bright: { key: 1.4 },
    lens: { lens: 1, key: .35, caus: .5, glow: .3 },
    // web3 site default (round 5): key light follows, toned down to slight, combined with Lift (deeper bend,
    // brighter rim, thicker-looking wall, softer shadow). On ink glass the moving light looks wrong, so ink gets Lift only.
    keylift: { follow: 1, key: .3, caus: .6, glow: .35, spread: .25, disp: 1.3, env: 1.4, wall: .35, shadow: 1 },
    liftink: { key: .12, spread: .4, caus: .8, disp: 1.3, env: 1.5, wall: .35, shadow: 1 }
  };
  var PRESS = { none: 0, soft: .45, flat: 1 };

  // a 3w x 3h displacement map with one round bulge in the middle; it is moved under the pointer with feImage x/y
  var lensCache = {};
  function lensMap(w, h) {
    var k = w + 'x' + h; if (lensCache[k]) return lensCache[k];
    var W3 = w * 3, H3 = h * 3, c = D.createElement('canvas'); c.width = W3; c.height = H3;
    var x2 = c.getContext('2d'), im = x2.createImageData(W3, H3), d = im.data, R = h * 1.05, cx = W3 / 2, cy = H3 / 2;
    for (var y = 0; y < H3; y++) for (var x = 0; x < W3; x++) {
      var dx = x + .5 - cx, dy = y + .5 - cy, r = Math.sqrt(dx * dx + dy * dy), t = r / R, m = 0, i = (y * W3 + x) * 4;
      if (t < 1 && r > .01) { var u = 1 - t * t; m = t * u * u / .3436; } // peak 1 at t ~ .45; samples move toward the centre: magnifies
      d[i] = clamp(128 - (r ? dx / r : 0) * m * 127, 0, 255); d[i + 1] = clamp(128 - (r ? dy / r : 0) * m * 127, 0, 255); d[i + 2] = 128; d[i + 3] = 255;
    }
    x2.putImageData(im, 0, 0);
    return (lensCache[k] = c.toDataURL());
  }

  function glassV3(btn, o) {
    if (btn._g3) return btn._g3;
    o = o || {};
    if (!styled) { var st = D.createElement('style'); st.textContent = CSSTXT; D.head.appendChild(st); styled = true; }
    var B = setupButton(btn, o), cv = B.cv, ctx = B.ctx, prim = B.prim, dark = B.dark;
    var w = 0, h = 0, r = 20, dpr = 1, sized = '', rim = [], tgt = null, cur = null, alpha = null, band = null, img = null, ok = false, opt = null;
    var api = { el: btn, variant: B.variant, ms: 0 }, lab = null, dmaps = [], lens = null, lastDisp = -1, lastXs = '';
    var inkish = prim && lum(B.fill) < .2 || dark; // dark glass: white light reads, so less of it is needed
    function mode() { var m = btn.getAttribute('data-hover') || 'keylift'; if (m === 'keylift' && inkish) m = 'liftink'; return HOVERS[m] ? m : 'base'; }
    /* slab geometry (round 5), from the button height: a short steep bevel (bez) and a wall of thickness (T)
       seen along the bottom edge, as if looking at a slab of glass slightly from above */
    // hover recipe from the panel's numbers (key light follows + Lift; ink normally Lift only)
    function recipe() { var v = inkish ? 'I' : 'W'; return { follow: P('follow' + v), key: P('keyH' + v), glow: P('glowH' + v), spread: P('spread' + v), caus: P('causH'), disp: P('liftBend'), env: P('liftRim'), wall: P('liftWall'), shadow: P('liftShadow') }; }
    function slab() { var sc = h / 44, bez = clamp(P('bevel') * sc, 1.5, h / 2 - 1), T = clamp(P('wall') * sc, 0, h / 3); return { bez: bez, T: T, depth: Math.min(h / 2 - 1, Math.max(bez, T * 1.35 + 8) + 1) }; }

    api.scan = function () {
      var R = btn.getBoundingClientRect(); ok = !!(R.width && R.height); if (!ok) return;
      // layout size, not the on-screen box: inside a scaled or tilted scene (the hero) they differ
      w = Math.round(btn.offsetWidth || R.width); h = Math.round(btn.offsetHeight || R.height);
      r = Math.min(parseFloat(getComputedStyle(btn).borderTopLeftRadius) || 0, h / 2);
      var bh = behind(btn, R, B.fg), surface = surfaceOf(B, bh.base, .38, .28);
      var S = sampleAround(btn, R, r, toLab(surface), w, h);
      var n = Math.max(24, Math.round((2 * w + 2 * h) / 3)), pts = rrPts(.5, .5, w - 1, h - 1, r - .5, n);
      var Lr = lightRim(pts, S, Math.max(6, P('falloff'))), a = rimAlpha(Lr, 2.4 * B.str * intensity() * (B.colour ? 1.6 : 1), .62, .11), Lt = prim || dark ? .84 : .7;
      // per rim point: the reflected colour + its alpha, then the light that comes THROUGH the glass from there
      // (hue lifted to a visible chroma, strength = the salient share seen there, ambient included)
      var t = new Float32Array(n * 8), Le = inkish ? .8 : .74;
      for (var i = 0; i < n; i++) {
        var c = reflectOn(B, Lr.A[i], Lr.B[i], Lt, .15), eC = inkish ? .12 : .11, eh = Math.sqrt(Lr.EA[i] * Lr.EA[i] + Lr.EB[i] * Lr.EB[i]) || 1e-6;
        var e = fromLab([Le, Lr.EA[i] / eh * eC, Lr.EB[i] / eh * eC]);
        t[i * 8] = c[0]; t[i * 8 + 1] = c[1]; t[i * 8 + 2] = c[2]; t[i * 8 + 3] = a[i];
        var ea0 = clamp(Math.max(sstep(0, .32, Lr.I[i]), sstep(P('pickLo'), Math.max(P('pickLo') + .05, P('pickHi')), Lr.EI[i])) * B.str * Math.min(1.6, intensity()) * P('pick'), 0, 1);
        // causFloor: with no colour in reach (a white page) the light through the glass is a cool neutral glass tone,
        // so the caustic and edge still read as glass, on purpose; real nearby colour takes over as it appears
        var fl = P('causFloor'), FC = inkish ? FLOOR_I : FLOOR_W;
        if (fl > 0 && ea0 < fl) { var kk = ea0 / fl; e = [FC[0] + (e[0] - FC[0]) * kk, FC[1] + (e[1] - FC[1]) * kk, FC[2] + (e[2] - FC[2]) * kk]; ea0 = fl; }
        t[i * 8 + 4] = e[0]; t[i * 8 + 5] = e[1]; t[i * 8 + 6] = e[2]; t[i * 8 + 7] = ea0;
      }
      if (!cur || cur.length !== t.length) cur = null;
      rim = pts; tgt = t;
      lab = liquidLabel(B, bh.worst); if (!prim) alpha = glassAlpha(B.fg, bh.worst, dark, B.fill, dark ? .28 : P('whiteA')); else if (B.tint) alpha = glassAlpha(B.fg, bh.worst, true, B.fill, B.fa, B.dim);
    };
    /* per band pixel optics from the lip's slope at that depth:
       fr: Fresnel reflectance (Schlick), sn: sin of the slope (the specular uses its power, so it hugs the edge),
       cw: caustic depth weight, a soft band about 40% of the lip in from the edge (where light through the far lip focuses) */
    function optics() {
      var sl = slab(), bez = sl.bez, m = band.ix.length, fr = new Float32Array(m), sn = new Float32Array(m), cw = new Float32Array(m), F0 = .04, e0 = null;
      function slope(d) { var t = Math.max(.004, d / bez), u = 1 - t, base = 1 - u * u * u * u; return Math.atan(Math.pow(base, -.75) * u * u * u * .55); }
      function fres(d) { var c = Math.cos(slope(d)); return F0 + (1 - F0) * Math.pow(1 - c, 2); }
      e0 = fres(.35);
      // caustic: light through the far lip focuses in a soft band just inside the wall (round 5: wider, so it reads)
      var dc = sl.T + P('causPos'), wc = Math.max(.3, P('causWidth'));
      for (var j = 0; j < m; j++) {
        var d = Math.max(0, band.d[j]);
        fr[j] = Math.min(1, fres(d) / e0); sn[j] = Math.sin(slope(d));
        var q = (d - dc) / wc; cw[j] = Math.exp(-q * q);
      }
      return { fr: fr, sn: sn, cw: cw, bez: bez, T: sl.T };
    }
    function collectMaps() {
      dmaps = []; lens = null; lastDisp = -1;
      if (!api.filter || !btn.classList.contains(PFX + '-warped')) return;
      var f = api.filter, kids = f.children, lastEl = kids[kids.length - 1];
      [].forEach.call(f.querySelectorAll('feDisplacementMap'), function (e) { dmaps.push({ e: e, s: +e.getAttribute('scale') }); });
      if (mode() === 'lens') {
        lastEl.setAttribute('result', 'lip');
        var NS = 'http://www.w3.org/2000/svg', fi = D.createElementNS(NS, 'feImage'), fd = D.createElementNS(NS, 'feDisplacementMap');
        fi.setAttribute('href', lensMap(w, h)); fi.setAttribute('width', w * 3); fi.setAttribute('height', h * 3); fi.setAttribute('preserveAspectRatio', 'none'); fi.setAttribute('result', 'lm');
        fd.setAttribute('in', 'lip'); fd.setAttribute('in2', 'lm'); fd.setAttribute('scale', '0'); fd.setAttribute('xChannelSelector', 'R'); fd.setAttribute('yChannelSelector', 'G');
        f.appendChild(fi); f.appendChild(fd); lens = { fi: fi, fd: fd, k: -1, x: 1e9, y: 1e9 };
      }
    }
    api.write = function () {
      if (!ok || !tgt) return;
      var key = w + 'x' + h + 'r' + r + 'n' + rim.length + (liquid() ? 'L' : 'S') + (btn._busy ? 'b' : '') + mode() + 'v' + (W.GLASS_P_V || 0);
      if (key !== sized) {
        sized = key; dpr = Math.min(W.devicePixelRatio || 1, 2);
        cv.width = Math.round(w * dpr); cv.height = Math.round(h * dpr);
        band = buildBand(w, h, r, dpr, rim, slab().depth); img = ctx.createImageData(cv.width, cv.height); opt = optics(); cur = null;
        applyRefract(api, B, btn, w, h, r, { snell: true, bez: opt.bez + 1.5, scale: (inkish ? P('refrI') : P('refrW')) * Math.min(B.str, 2.5) * h / 44, post: 'blur(' + P('refrBlur') + 'px) saturate(' + P('refrSat') + ')', fallback: 'blur(3px) saturate(1.5) brightness(1.03)' });
        collectMaps();
      }
      if (alpha != null) btn.style.setProperty('--g3-a', alpha.toFixed(2));
      applyMode(btn, lab);
      if (!cur) cur = tgt.slice();
      anim.kick();
    };

    function shimmer(u, t) { // sharp bright ridges that drift slowly along the rim, like light through moving water
      var a = .5 + .5 * Math.sin(6.2832 * u * 3 + t * .9 + 1.2 * Math.sin(6.2832 * u * 2 - t * .5)), b = .5 + .5 * Math.sin(6.2832 * u * 5 - t * .7);
      a *= a; b *= b; b *= b;
      return Math.min(1, a * .8 + b * .45);
    }
    function draw(s) {
      var t0 = performance.now();
      ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.clearRect(0, 0, cv.width, cv.height);
      if (!cur || !band) return;
      var md = mode(), H = md === 'keylift' || md === 'liftink' ? recipe() : HOVERS[md], k = P('L_hover') > 0 ? s.k : 0, q = s.p * (PRESS[btn.getAttribute('data-press')] != null ? PRESS[btn.getAttribute('data-press')] : PRESS.soft);
      function m(v) { return 1 + (H[v] || 0) * k; }
      var kKey = m('key') * (1 - .6 * q), kEnv = m('env') * (1 - .5 * q), kCaus = m('caus') * (1 - .8 * q), kDisp = m('disp') * (1 - .55 * q);
      var glow = (H.glow || 0) * k * (1 - q), spread = (H.spread || 0) * k * (1 - q), shim = (H.shim || 0) * k, follow = (H.follow || 0) * k, lensK = (H.lens || 0) * k * (1 - .7 * q);
      var shadow = (H.shadow || 0) * k * (1 - q) - .6 * q;
      // light direction: the fixed key light, turned toward the pointer as hover comes in (option "key")
      var Lx = KEY[0], Ly = KEY[1];
      if (follow > .001) {
        var vx = (s.sx - w / 2) / (w / 2), vy = (s.sy - h / 2) / (h / 2) - .35, mm = Math.sqrt(vx * vx + vy * vy);
        if (mm > .05) { vx /= mm; vy /= mm; Lx += (vx - Lx) * follow; Ly += (vy - Ly) * follow; mm = Math.sqrt(Lx * Lx + Ly * Ly) || 1; Lx /= mm; Ly /= mm; }
      }
      var D2 = img.data, gs = Math.max(6, h * P('glowSize')), sig = 2 * gs * gs, keyA = (inkish ? P('keyI') : P('keyW')) * kKey * P('L_key'), envK = (prim ? 1.7 : 1.6) * kEnv;
      // caustic strength. Round 5: tinted by the light coming through (it was white, so on white glass it vanished)
      var causA = (inkish ? P('causI') : P('causW')) * kCaus * (liquid() ? 1.3 : 1) * P('L_caus'), spP = Math.max(.5, P('keyTight') - 1.7 * spread), n = rim.length;
      // the wall: thickness seen along the bottom edge. Lift makes the slab read a little thicker; nothing resizes
      var T = opt.T * (1 + (H.wall || 0) * k * (1 - q)), wallA = (inkish ? P('wallI') : P('wallW')) * P('L_wall'), crestA = (inkish ? P('crestI') : P('crestW')) * P('L_crest'), glowA = (inkish ? P('glowI') : P('glowW')) * kEnv * P('L_glow'), gW = Math.max(.3, P('glowWidth')), cPow = P('causPow'), cEnv = P('causEnv'), cDown = P('causDown'), cTint = P('causTint'), wTint = P('wallTint'), Lrefl = P('L_refl');
      var WB = inkish ? [86, 86, 94] : [201, 207, 216];
      var oR, oG, oB, oA;
      function over(r2, g2, b2, a2) { if (a2 <= .001) return; var na = a2 + oA * (1 - a2); oR = (r2 * a2 + oR * oA * (1 - a2)) / na; oG = (g2 * a2 + oG * oA * (1 - a2)) / na; oB = (b2 * a2 + oB * oA * (1 - a2)) / na; oA = na; }
      D2.fill(0);
      for (var j = 0; j < band.ix.length; j++) {
        var d = band.d[j], ri = band.ri[j], qq = ri * 8, cov = clamp(d * dpr + .5, 0, 1), ny = band.ny[j];
        var nl = band.nx[j] * Lx + band.ny[j] * Ly, sn = d < opt.bez ? opt.sn[j] : 0, ea = cur[qq + 7];
        var a1 = d < opt.bez ? Math.min(1, cur[qq + 3] * opt.fr[j] * envK) * cov : 0;                    // reflected environment
        var sp = Math.pow(sn, spP) * (Math.pow(Math.max(0, nl), 2) + .35 * Math.pow(Math.max(0, -nl), 2)) * keyA; // key light on the lip
        var ca = opt.cw[j] * Math.pow(Math.max(0, -nl), cPow) * causA * (1 - cEnv + cEnv * ea) * (1 - cDown + cDown * sstep(.2, .95, ny)); // mostly along the bottom                // caustic, opposite the light
        if (shim > .001) { var sh = shimmer(ri / n, s.t); sp *= 1 + shim * (1.8 * sh - .2); ca *= 1 + shim * (.7 * sh - .15); }
        if (glow > .001 && sn > 0) { var dx = rim[ri].x - s.sx, dy = rim[ri].y - s.sy; sp += Math.exp(-(dx * dx + dy * dy) / sig) * glow * opt.fr[j] * sn; }
        // wall + crest: only where the edge faces down
        var down = sstep(.45, .97, ny), wl = down * clamp(T - d + .5, 0, 1), cr = down * Math.exp(-Math.pow((d - T) / .55, 2));
        oR = oG = oB = oA = 0;
        // light that came in through the side of the slab: a soft glow of the nearby hue just inside the edge
        if (ea > .01 && glowA > 0) over(cur[qq + 4], cur[qq + 5], cur[qq + 6], ea * glowA * Math.exp(-d / gW) * cov);
        if (Lrefl > 0) over(cur[qq], cur[qq + 1], cur[qq + 2], a1);
        if (wl > .001) { var tw = Math.min(1, (inkish ? .7 : .8) * ea * wTint); over(WB[0] + (cur[qq + 4] - WB[0]) * tw, WB[1] + (cur[qq + 5] - WB[1]) * tw, WB[2] + (cur[qq + 6] - WB[2]) * tw, wl * wallA * (.8 + .4 * ea) * cov); }
        if (cr > .001) over(255, 255, 255, cr * crestA);
        if (ca > .001) { var tc = Math.min(1, (inkish ? .55 : .85) * cTint * Math.min(1, ea * 1.4 + (cEnv ? 0 : .4))); over(255 + (cur[qq + 4] - 255) * tc, 255 + (cur[qq + 5] - 255) * tc, 255 + (cur[qq + 6] - 255) * tc, Math.min(1, ca)); }
        if (sp > .001) over(255, 255, 255, Math.min(1, sp) * cov);
        if (oA < .003) continue;
        var p = band.ix[j];
        D2[p] = oR; D2[p + 1] = oG; D2[p + 2] = oB; D2[p + 3] = oA * 255;
      }
      ctx.putImageData(img, 0, 0);
      if (glow > .001) { // a faint wash of the pointer's light across the face
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0); ctx.save(); rrPath(ctx, 0, 0, w, h, r); ctx.clip();
        var g = ctx.createRadialGradient(s.sx, s.sy, 0, s.sx, s.sy, Math.max(w, h) * .55);
        g.addColorStop(0, rgba(WHITE, glow * (inkish ? .05 : .16))); g.addColorStop(1, rgba(WHITE, 0));
        ctx.fillStyle = g; ctx.fillRect(0, 0, w, h); ctx.restore();
      }
      if (q > .001 && inkish) { ctx.setTransform(dpr, 0, 0, dpr, 0, 0); rrPath(ctx, 0, 0, w, h, r); ctx.fillStyle = rgba([0, 0, 0], .12 * q); ctx.fill(); }
      // refraction depth (lift, press) and the moving lens: attribute writes only when they change
      if (dmaps.length && Math.abs(kDisp - lastDisp) > .004) { lastDisp = kDisp; dmaps.forEach(function (x) { x.e.setAttribute('scale', (x.s * kDisp).toFixed(2)); }); }
      if (lens) {
        var ls = (lensK * clamp(h * .85, 24, 40)).toFixed(1);
        if (ls !== lens.k) { lens.k = ls; lens.fd.setAttribute('scale', ls); }
        var lx = Math.round(s.sx - 1.5 * w), ly = Math.round(s.sy - 1.5 * h);
        if (lx !== lens.x || ly !== lens.y) { lens.x = lx; lens.y = ly; lens.fi.setAttribute('x', lx); lens.fi.setAttribute('y', ly); }
      }
      // lift reads as a softer, wider shadow; press as a tighter one. The box never changes.
      var xs = Math.abs(shadow) < .01 ? '' : shadow > 0 ?
        '0 ' + (2 + 8 * shadow).toFixed(1) + 'px ' + (6 + 20 * shadow).toFixed(1) + 'px rgba(0,0,0,' + ((inkish ? .12 : .07) * shadow).toFixed(3) + ')' :
        'inset 0 1px ' + (3 * -shadow).toFixed(1) + 'px rgba(0,0,0,' + (.12 * -shadow).toFixed(3) + ')';
      if (xs !== lastXs) { lastXs = xs; if (xs) btn.style.setProperty('--g3-xs', xs); else btn.style.removeProperty('--g3-xs'); }
      api.ms = api.ms ? api.ms * .9 + (performance.now() - t0) * .1 : performance.now() - t0;
    }
    var anim = animator(btn, draw, function (f) { return cur && tgt && cur.length === tgt.length ? easeArr(cur, tgt, f) : false; }, function () { return mode() === 'shimmer'; });
    api.state = anim; api.kick = anim.kick;
    api.update = function () { refresh(true); };
    btn._g3 = api; register(api); refresh();
    return api;
  }
  /* ---------- the glass nav's lower edge (round 5) ----------
     .site-nav::after (aero.css) is a strip of thicker, clearer glass along the bottom of the frosted nav. In Chromium
     an SVG filter on it pulls what passes under it toward the strip's centre line, the way the rounded edge of a
     sheet of glass magnifies. Sized per nav in px; rebuilt when the width changes. data-glass-nav="off" opts out. */
  var navUid = 0;
  function navEdge() {
    if (!REFRACT) return;
    [].forEach.call(D.querySelectorAll('.site-nav'), function (nav) {
      if (nav.getAttribute('data-glass-nav') === 'off') return;
      var w = Math.round(nav.getBoundingClientRect().width), eh = Math.round(parseFloat(getComputedStyle(nav, '::after').height)) || 0;
      if (!w || !eh) return;
      var st = nav._g3n || (nav._g3n = { id: PFX + 'nav' + (++navUid) }), key = w + 'x' + eh + 's' + P('navStrip');
      if (st.key === key) return; st.key = key;
      var H4 = eh * 4, c = D.createElement('canvas'); c.width = 2; c.height = H4;
      var x2 = c.getContext('2d'), im = x2.createImageData(2, H4), d = im.data;
      for (var y = 0; y < H4; y++) {
        var v = ((y + .5) / H4 - .5) * 2, m = -v * (1 - .35 * v * v); // -1 top .. 1 bottom; samples move toward the centre line
        for (var x = 0; x < 2; x++) { var i = (y * 2 + x) * 4; d[i] = 128; d[i + 1] = clamp(128 + m * 127, 0, 255); d[i + 2] = 128; d[i + 3] = 255; }
      }
      x2.putImageData(im, 0, 0);
      svgFilter(st, w, eh, '<feImage href="' + c.toDataURL() + '" x="0" y="0" width="' + w + '" height="' + eh + '" preserveAspectRatio="none" result="m"/>' +
        '<feDisplacementMap in="SourceGraphic" in2="m" scale="' + (eh * P('navStrip')).toFixed(1) + '" xChannelSelector="R" yChannelSelector="G"/>');
      nav.style.setProperty('--g3-nav-edge', 'url(#' + st.filter.id + ')');
    });
  }
  var runBase = run;
  run = function () { navEdge(); runBase(); };
  boot(glassV3, 'GlassV3');
}
